import { FormikErrors, FormikTouched, useFormik } from 'formik';
import Input from '@/components/Input/Input';
import { login, LoginResponse, respondToAuthChallenge } from '@/core/api';
import { isEmail, setTokensToLocalStorage } from '@/core/helpers';
import './Login.scss';
import React, { ChangeEventHandler, useState } from 'react';
import { Loader } from '@/components/Loader/Loader';
import { Trans, useTranslation } from 'react-i18next';
import {
  getAmazonConnectCredentials,
  getUser,
  getUserCompanies,
} from '@/core/api';
import Header from '@/components/Header/Header';
import { PublicRoutesEnum } from '@/core/enums';
import { Link } from 'react-router';
import { useAuth } from '../../hooks';
import Button from '@/components/Button';
import RegisterVerification from '../RegisterUser/RegisterVerification';
import MFAInput from './MFAInput';
import { DEFAULT_ERROR } from '@/components/Notifications/Notification';
import classNames from 'classnames';
import { getCognitoUser } from '@/core/cognito';
import { useFlags } from 'launchdarkly-react-client-sdk';

interface FormValues {
  email: string;
  password: string;
  code: string;
  session: string;
  challenge_name: string;
}

const Login = () => {
  const { t, i18n } = useTranslation();
  const { vs8208SigninImprovments } = useFlags();
  const [isLoading, setIsLoading] = useState(false);
  const { authUser } = useAuth();
  const [error, setError] = useState<string | React.ReactNode>('');
  const [isValidateEmailShown, setIsValidateEmailShown] = useState(false);
  const [isMfaShown, setIsMfaShown] = useState(false);

  const formik = useFormik({
    validateOnBlur: true,
    initialValues: {
      email: '',
      password: '',
      code: '',
      session: '',
      challenge_name: '',
    },
    validate: (values) => {
      const errors: FormikErrors<FormValues> = {};
      if (!values.email) {
        errors.email = t('Required')!;
      }
      if (values.email !== '' && !isEmail(values.email)) {
        errors.email = t(`Email is not valid`)!;
      }
      if (!values.password) {
        errors.password = t('Required')!;
      }

      return errors;
    },
    onSubmit: async ({ email, password, code, session, challenge_name }) => {
      if (error) setError('');
      setIsLoading(true);

      try {
        let res: LoginResponse;

        if (isMfaShown) {
          res = await respondToAuthChallenge({
            username: email,
            code,
            session,
            challenge_name,
          });
        } else {
          res = await login(email, password);
          if (res.challenge_name) {
            setIsMfaShown(true);
            formik.setFieldValue('session', res.session);
            formik.setFieldValue('challenge_name', res.challenge_name);
            return setIsLoading(false);
          }
        }

        setTokensToLocalStorage(
          res.access_token!,
          res.refresh_token!,
          res.id_token!
        );

        const responses = await Promise.all([
          getUser(),
          getAmazonConnectCredentials(),
          getUserCompanies(),
          getCognitoUser(),
        ]);

        authUser(responses);

        setIsLoading(false);
      } catch (err: any) {
        const supportEmail = 'support@countx.com';
        setIsLoading(false);

        if (
          err.response &&
          ((err.response.data.code === 'NotAuthorizedException' &&
            err.response.data.message === 'User is disabled.') ||
            err.response.data.code === 'AccountPermanentlyLocked')
        ) {
          setError(
            <Trans
              i18nKey='Account permanently locked'
              values={{ email: supportEmail }}
              components={{
                emailLink: (
                  <a
                    href={`mailto:${supportEmail}`}
                    className='text-blue-500 underline hover:text-blue-600'
                  />
                ),
              }}
            />
          );
        } else if (
          err.response &&
          (err.response.data.code === 'NotAuthorizedException' ||
            err.response.data.code === 'UserNotFoundException')
        ) {
          if (isMfaShown) {
            setIsMfaShown(false);
          }
          if (vs8208SigninImprovments) {
            const remainingAttempts = err.response.data.remaining_attempts;
            if (!isMfaShown && remainingAttempts != null && remainingAttempts <= 3) {
              setError('Login attempt warning');
            } else {
              setError(
                isMfaShown ? DEFAULT_ERROR : 'Incorrect username or password'
              );
            }
          } else {
            setError(
              isMfaShown ? DEFAULT_ERROR : 'Incorrect username or password'
            );
          }
        } else if (
          err.response &&
          err.response.data.code === 'AccountTemporarilyLocked'
        ) {
          setError(
            vs8208SigninImprovments
              ? 'Account temporarily locked message'
              : 'Account temporarily locked'
          );
        } else if (
          err.response &&
          err.response.data.code === 'UserNotConfirmedException'
        ) {
          setIsValidateEmailShown(true);
        } else if (
          err.response &&
          err.response.data.code === 'CodeMismatchException'
        ) {
          setError('Incorrect verification code');
        } else {
          setError('Something went wrong, please try again');
        }
      }
    },
  });

  const onChangeField: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (error) setError('');
    formik.setFieldValue(
      e.target.name,
      e.target.value,
      formik.touched[e.target.name as keyof FormikTouched<FormValues>]
    );
  };

  if (isLoading) return <Loader />;

  if (isValidateEmailShown) {
    return (
      <RegisterVerification
        email={formik.values.email}
        password={formik.values.password}
      />
    );
  }

  return (
    <>
      <Header />
      <div
        className='flex min-h-[calc(100vh-91px)] items-center justify-center bg-white px-4 py-8 lg:px-6 2xl:px-14'
        data-testid='login'
      >
        <div className='Login__container lg:mb-12 lg:pb-12'>
          {isMfaShown ? (
            <>
              <div
                className={classNames('mb-4 text-center text-h3 font-black', {
                  'text-h2': i18n.language === 'en',
                })}
              >
                {t('2-step authentication')}
              </div>
              <div className='mb-6 whitespace-pre-line text-center'>
                {t('Get a verification code from authenticator app')}
              </div>
              <form
                onSubmit={formik.handleSubmit}
                noValidate
                className='w-full'
              >
                <MFAInput
                  onChange={(code) => formik.setFieldValue('code', code)}
                  error={!!error}
                />
                {error && (
                  <div className='my-6 text-center text-red-500'>
                    {typeof error === 'string' ? t(error) : error}
                  </div>
                )}
                <Button
                  className='Login__btn mt-4 !w-full'
                  type='submit'
                  disabled={isLoading}
                >
                  {t('Continue')}
                </Button>
              </form>
            </>
          ) : (
            <>
              <div className='Title--lg mb-6 font-black lg:text-center'>
                {t('Welcome')}
              </div>
              <form
                onSubmit={formik.handleSubmit}
                noValidate
                className='w-full'
              >
                <div className='Login__content items-start'>
                  <Input
                    label='Email'
                    placeholder='Enter your email'
                    name='email'
                    id='email'
                    onChange={onChangeField}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                    error={formik.errors.email}
                    touched={formik.touched.email}
                  />
                  <Input
                    wrapperClassName='mt-4'
                    type='password'
                    label='Password'
                    placeholder='Enter your password'
                    name='password'
                    id='password'
                    onChange={onChangeField}
                    onBlur={formik.handleBlur}
                    value={formik.values.password}
                    touched={formik.touched.password}
                    error={formik.errors.password}
                  />
                </div>
                <div>
                  <Button
                    className='Login__btn mt-4 !w-full'
                    type='submit'
                    disabled={isLoading}
                  >
                    {t('Sign In')}
                  </Button>
                  {error && (
                    <div
                      className={classNames(
                        'Login__error mt-2 text-center',
                        {
                          'Login__error--legacy': !vs8208SigninImprovments,
                        }
                      )}
                    >
                      {typeof error === 'string' ? t(error) : error}
                    </div>
                  )}
                </div>
              </form>
              <div className='Text mt-4 pt-2 text-center'>
                <Link
                  to={PublicRoutesEnum.ForgotPassword}
                  className='text-blue-500 transition-colors hover:text-blue-600'
                >
                  {t('I forgot my password')}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Login;
